import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  defaultHero,
  defaultHeader,
  defaultFirstSection,
  defaultArtSection,
  defaultNowSection,
  defaultGallerySection,
  defaultVideosSection,
  defaultContactSection,
  defaultNewSection,
  defaultGlobalColors,
  defaultEventsSection,
} from '../data/defaultSections';
import { fetchSections, saveSectionRequest } from '../api/sections';

export const LandingPageContext = createContext();

export const useLandingPage = () => useContext(LandingPageContext);

// The API stores localized values as { de: ..., en: ... }; pick the array for the active edit language.
const resolveImagesField = (section, language) => {
  if (!section) return section;
  const normalized = { ...section };
  if ('images' in normalized) {
    const val = normalized.images;
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      normalized.images =
        (Array.isArray(val[language]) && val[language]) ||
        Object.values(val).find((v) => Array.isArray(v)) ||
        [];
    } else if (!val) {
      normalized.images = [];
    }
  }
  return normalized;
};

const hasImages = (section) => Array.isArray(section?.images) && section.images.length > 0;

export const LandingPageProvider = ({ children }) => {
  const [hero, setHero] = useState(defaultHero);
  const [header] = useState(defaultHeader);
  const [firstSection, setFirstSection] = useState(defaultFirstSection);
  const [artSection, setArtSection] = useState(defaultArtSection);
  const [nowSection, setNowSection] = useState(defaultNowSection);
  const [gallerySection, setGallerySection] = useState(defaultGallerySection);
  const [videosSection, setVideosSection] = useState(defaultVideosSection);
  const [contactSection, setContactSection] = useState(defaultContactSection);
  const [newSection, setNewSection] = useState(defaultNewSection);
  const [globalColors, setGlobalColors] = useState(defaultGlobalColors);
  const [eventsSection, setEventsSection] = useState(defaultEventsSection);
  const [isLoading, setIsLoading] = useState(true);
  const [editLanguage, setEditLanguage] = useState('de');
  const scrollSmootherRef = useRef(null);

  // Stable registry: section id -> setter. Keeps the public API generic (updateSection/saveSection)
  // so adding a section never requires touching this file's consumers.
  const SETTERS = {
    hero: setHero,
    firstSection: setFirstSection,
    artSection: setArtSection,
    nowSection: setNowSection,
    gallerySection: setGallerySection,
    videosSection: setVideosSection,
    contactSection: setContactSection,
    newSection: setNewSection,
    globalColors: setGlobalColors,
    eventsSection: setEventsSection,
  };

  // Mirror of the latest committed section state, readable inside stable callbacks.
  const sectionsRef = useRef(null);
  sectionsRef.current = {
    hero,
    firstSection,
    artSection,
    nowSection,
    gallerySection,
    videosSection,
    contactSection,
    newSection,
    globalColors,
    eventsSection,
  };

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        const sections = await fetchSections();
        if (cancelled || !sections) return;

        setHero(sections.hero || defaultHero);
        setFirstSection(sections.firstSection || defaultFirstSection);

        const apiArt = resolveImagesField(sections.artSection, editLanguage);
        setArtSection(hasImages(apiArt) ? apiArt : defaultArtSection);

        const apiNow = resolveImagesField(sections.nowSection, editLanguage);
        setNowSection(hasImages(apiNow) ? apiNow : defaultNowSection);

        const apiGallery = resolveImagesField(sections.gallerySection, editLanguage);
        setGallerySection(hasImages(apiGallery) ? apiGallery : defaultGallerySection);

        setVideosSection(sections.videosSection || defaultVideosSection);
        setContactSection(sections.contactSection || defaultContactSection);
        setNewSection(sections.newSection || defaultNewSection);
        setGlobalColors(sections.globalColors || defaultGlobalColors);
        setEventsSection(sections.eventsSection || defaultEventsSection);
      } catch (error) {
        console.error('Failed to load data from API, using defaults:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [editLanguage]);

  /** Replace the full data of one section. `next` may be a plain object or an updater function. */
  const updateSection = useCallback((sectionId, next) => {
    const setter = SETTERS[sectionId];
    if (!setter) throw new Error(`Unknown section: ${sectionId}`);
    setter(typeof next === 'function' ? next : () => next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Persist one section to the API. Reads the latest committed state unless
   * explicit data is passed (needed right after updateSection in the same tick).
   */
  const saveSection = useCallback(async (sectionId, dataOverride) => {
    const data = dataOverride !== undefined ? dataOverride : sectionsRef.current[sectionId];
    if (data === undefined) return false;
    try {
      await saveSectionRequest(sectionId, data);
      return true;
    } catch (error) {
      console.error(`Failed to save ${sectionId} to API:`, error);
      return false;
    }
  }, []);

  const setScrollSmoother = useCallback((smoother) => {
    scrollSmootherRef.current = smoother;
  }, []);

  const getScrollSmoother = useCallback(() => scrollSmootherRef.current, []);

  const scrollTo = useCallback((target, options = {}) => {
    const offsetFromOptions =
      options && typeof options.offset !== 'undefined' ? Number(options.offset) || 0 : null;
    const computeHeaderOffset = () => {
      try {
        if (typeof document === 'undefined') return 28;
        const headerEl = document.querySelector('.header');
        if (!headerEl) return 28;
        const rect = headerEl.getBoundingClientRect();
        return Math.max(0, Math.round(rect.top)) + Math.round(rect.height || 0) + 8;
      } catch {
        return 28;
      }
    };
    const offset = offsetFromOptions !== null ? offsetFromOptions : computeHeaderOffset();
    const smoother = scrollSmootherRef.current;
    try {
      let el;
      if (typeof target === 'string') el = document.querySelector(target);
      else if (target instanceof HTMLElement) el = target;
      if (!el) return;
      const absoluteY = () =>
        el.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0) - offset;

      if (smoother && typeof smoother.scrollTo === 'function') {
        const scrollerEl = smoother.scroller;
        if (scrollerEl instanceof HTMLElement) {
          const elRect = el.getBoundingClientRect();
          const scrollerRect = scrollerEl.getBoundingClientRect();
          smoother.scrollTo(Math.max(0, scrollerEl.scrollTop + elRect.top - scrollerRect.top - offset), true);
        } else {
          smoother.scrollTo(absoluteY(), true);
        }
      } else {
        window.scrollTo({ top: absoluteY(), behavior: options.behavior || 'smooth' });
      }
    } catch (err) {
      console.error('Scroll error:', err);
    }
  }, []);

  const value = {
    hero,
    header,
    firstSection,
    artSection,
    nowSection,
    gallerySection,
    videosSection,
    contactSection,
    newSection,
    globalColors,
    eventsSection,
    isLoading,
    editLanguage,
    setEditLanguage,
    updateSection,
    saveSection,
    setScrollSmoother,
    getScrollSmoother,
    scrollTo,
  };

  return <LandingPageContext.Provider value={value}>{children}</LandingPageContext.Provider>;
};
